import { useEffect, useMemo, useRef, useState } from "react";
import { MdArrowDropDown, MdClose, MdSearch } from "react-icons/md";
import "./MultiUserSelect.css";

/**
 * Multi-select staff dropdown for report filters.
 * value: string[] of user ids
 * onChange: (nextIds: string[]) => void
 */
const MultiUserSelect = ({
  users = [],
  value = [],
  onChange,
  placeholder = "All",
  searchPlaceholder = "Search staff…",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  const selectedIds = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q),
    );
  }, [users, query]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedSet.has(String(u._id))),
    [users, selectedSet],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggleId = (id) => {
    const sid = String(id);
    const next = selectedSet.has(sid)
      ? selectedIds.filter((x) => x !== sid)
      : [...selectedIds, sid];
    onChange?.(next);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange?.([]);
  };

  const summary =
    selectedUsers.length === 0
      ? placeholder
      : selectedUsers.length === 1
        ? selectedUsers[0].name || "1 selected"
        : `${selectedUsers.length} selected`;

  return (
    <div className="mus-root" ref={rootRef}>
      <button
        type="button"
        className={`mus-trigger form-control${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`mus-summary${selectedUsers.length ? " has-value" : ""}`}>
          {summary}
        </span>
        <span className="mus-trigger-actions">
          {selectedUsers.length > 0 && (
            <span
              className="mus-clear"
              role="button"
              tabIndex={0}
              title="Clear"
              onClick={clearAll}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") clearAll(e);
              }}
            >
              <MdClose size={15} />
            </span>
          )}
          <MdArrowDropDown size={20} />
        </span>
      </button>

      {open && (
        <div className="mus-panel" role="listbox">
          <div className="mus-search">
            <MdSearch size={16} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
            />
          </div>
          <div className="mus-list">
            {filteredUsers.length === 0 ? (
              <div className="mus-empty">No staff found</div>
            ) : (
              filteredUsers.map((u) => {
                const id = String(u._id);
                const checked = selectedSet.has(id);
                return (
                  <label key={id} className={`mus-option${checked ? " is-checked" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleId(id)}
                    />
                    <span className="mus-option-name">{u.name || "Unnamed"}</span>
                  </label>
                );
              })
            )}
          </div>
          {selectedUsers.length > 0 && (
            <div className="mus-footer">
              <button type="button" className="mus-footer-clear" onClick={clearAll}>
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiUserSelect;
